import React, { useState } from 'react';
import { Tabs, Table, DatePicker, Button, Space } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { GET_RECHARGE_RECORDS } from 'graphql/queries/rechargeRecords';
import { GET_USAGE_RECORDS } from 'graphql/queries/usageRecords';
import { PagedRechargeRecord, PagedUsageRecord, RechargeRecord, UsageRecord } from 'graphql/types';
import { usePermission } from 'components/PermissionProvider';
import { exportUsageRecords } from 'utils/portalRestClient';

const { RangePicker } = DatePicker;

const getDefaultDateRange = (): [Dayjs, Dayjs] => [
    dayjs().subtract(6, 'day').startOf('day'),
    dayjs().endOf('day'),
];

const useRangePresets = () => {
    const { t } = useTranslation();
    return [
        { label: t('usageRecords.filters.presets.last7Days'), value: getDefaultDateRange() as [Dayjs, Dayjs] },
        { label: t('usageRecords.filters.presets.last30Days'), value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] as [Dayjs, Dayjs] },
        { label: t('usageRecords.filters.presets.thisMonth'), value: [dayjs().startOf('month'), dayjs().endOf('day')] as [Dayjs, Dayjs] },
        { label: t('usageRecords.filters.presets.lastMonth'), value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] as [Dayjs, Dayjs] },
    ];
};

const RechargeTab: React.FC = () => {
    const { t } = useTranslation();
    const presets = useRangePresets();
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(getDefaultDateRange());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data, loading, refetch } = useQuery<{ portalRechargeRecords: PagedRechargeRecord }>(
        GET_RECHARGE_RECORDS,
        {
            variables: {
                startDate: dateRange[0]?.format('YYYY-MM-DD'),
                endDate: dateRange[1]?.format('YYYY-MM-DD'),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                page: page - 1,
                size: pageSize,
            },
        }
    );

    const handleSearch = () => {
        setPage(1);
        refetch();
    };

    const columns = [
        {
            title: t('usageRecords.recharge.rechargeTime'),
            dataIndex: 'rechargeTime',
            key: 'rechargeTime',
            render: (val: string) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: t('usageRecords.recharge.rechargeAmount'),
            dataIndex: 'rechargeAmount',
            key: 'rechargeAmount',
        },
        {
            title: t('usageRecords.recharge.currency'),
            dataIndex: 'currency',
            key: 'currency',
        },
    ];

    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-gray-600">{t('usageRecords.filters.queryTime')}</span>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => setDateRange((dates ?? [null, null]) as [Dayjs | null, Dayjs | null])}
                    presets={presets}
                />
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                    {t('usageRecords.filters.search')}
                </Button>
            </div>
            <Table<RechargeRecord>
                columns={columns}
                dataSource={data?.portalRechargeRecords?.content}
                loading={loading}
                rowKey="id"
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.portalRechargeRecords?.totalElements,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total}`,
                }}
            />
        </div>
    );
};

const UsageTab: React.FC = () => {
    const { t } = useTranslation();
    const { hasPermission } = usePermission();
    const presets = useRangePresets();
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(getDefaultDateRange());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [exporting, setExporting] = useState(false);

    const { data, loading, refetch } = useQuery<{ portalUsageRecords: PagedUsageRecord }>(
        GET_USAGE_RECORDS,
        {
            variables: {
                startDate: dateRange[0]?.format('YYYY-MM-DD'),
                endDate: dateRange[1]?.format('YYYY-MM-DD'),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                page: page - 1,
                size: pageSize,
            },
        }
    );

    const handleSearch = () => {
        setPage(1);
        refetch();
    };

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            await exportUsageRecords({
                startDate: dateRange[0]?.format('YYYY-MM-DD'),
                endDate: dateRange[1]?.format('YYYY-MM-DD'),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
        } finally {
            setExporting(false);
        }
    };

    const columns = [
        {
            title: t('usageRecords.usage.usageTime'),
            dataIndex: 'usageTime',
            key: 'usageTime',
            render: (val: string) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: t('usageRecords.usage.orderNumber'),
            dataIndex: 'orderNumber',
            key: 'orderNumber',
        },
        {
            title: t('usageRecords.usage.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
        },
        {
            title: t('usageRecords.usage.usageAmount'),
            dataIndex: 'usageAmount',
            key: 'usageAmount',
        },
        {
            title: t('usageRecords.usage.currency'),
            dataIndex: 'currency',
            key: 'currency',
        },
        {
            title: t('usageRecords.usage.isRefunded'),
            dataIndex: 'isRefunded',
            key: 'isRefunded',
            render: (val: boolean) => val ? t('usageRecords.usage.refundedYes') : t('usageRecords.usage.refundedNo'),
        },
    ];

    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-gray-600">{t('usageRecords.filters.queryTime')}</span>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => setDateRange((dates ?? [null, null]) as [Dayjs | null, Dayjs | null])}
                    presets={presets}
                />
                <Space>
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        {t('usageRecords.filters.search')}
                    </Button>
                    {hasPermission('portal:usage-records:export') && (
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={exporting}
                            onClick={handleExport}
                        >
                            {t('usageRecords.filters.export')}
                        </Button>
                    )}
                </Space>
            </div>
            <Table<UsageRecord>
                columns={columns}
                dataSource={data?.portalUsageRecords?.content}
                loading={loading}
                rowKey="id"
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.portalUsageRecords?.totalElements,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total}`,
                }}
            />
        </div>
    );
};

const UsageRecordsPage: React.FC = () => {
    const { t } = useTranslation();

    const items = [
        {
            key: 'recharge',
            label: t('usageRecords.tabs.recharge'),
            children: <RechargeTab />,
        },
        {
            key: 'usage',
            label: t('usageRecords.tabs.usage'),
            children: <UsageTab />,
        },
    ];

    return (
        <div className="bg-white rounded-lg p-6">
            <Tabs items={items} />
        </div>
    );
};

export default UsageRecordsPage;
